import type { TranslationTable } from "@repo/shared/types";
import type { ComponentType } from "@/lib/types/flow";
import type { FlowComponentCategory } from "./flow-component-types";

// Localized category labels
export const CATEGORY_LABELS: TranslationTable<Record<FlowComponentCategory, string>> = {
  en_GB: {
    input: "Input",
    portfolio: "Portfolio",
    costs: "Costs",
    client: "Client",
    formatting: "Formatting",
  },
  nb_NO: {
    input: "Inndata",
    portfolio: "Portefølje",
    costs: "Kostnader",
    client: "Kunde",
    formatting: "Formatering",
  },
};

type LocalizedComponentInfo = {
  label: string;
  description: string;
  detailedDescription: string;
};

// Localized component type info
export const COMPONENT_TYPE_LABELS: TranslationTable<
  Record<ComponentType, LocalizedComponentInfo>
> = {
  en_GB: {
    field: {
      label: "Field Input",
      description: "Add an input field from your flow's field library",
      detailedDescription:
        "Display a form field that collects data from the user. Choose from fields defined in your flow's field library. Supports various input types including text, number, date, dropdown, and more.",
    },
    goal_input: {
      label: "Goal Input Form",
      description: "Form for editing goal-level details (type, name, description)",
      detailedDescription:
        "A form that allows advisors to configure goal-level details — the goal type (picked from the portfolio collection's goal types when configured), name, and an optional description. Investment schedule, amounts, and portfolio type are configured per portfolio with the Portfolio Input Form. Belongs on a goal page.",
    },
    portfolio_input: {
      label: "Portfolio Input Form",
      description: "Form for editing a portfolio's schedule, amounts and type",
      detailedDescription:
        "A form that allows advisors to configure a portfolio: its name, investment schedule (one-time or recurring deposits) and amounts, and the portfolio type selection (model or custom). Belongs on the portfolio page; rendered once per portfolio.",
    },
    custom_portfolio: {
      label: "Custom Portfolio Builder",
      description: "Build custom portfolios by selecting products",
      detailedDescription:
        "An interactive component that allows advisors to build custom portfolios by searching and selecting products, then specifying investment amounts or percentages for each. Includes options to show ISIN and product type columns.",
    },
    risk_assessment: {
      label: "Risk Assessment",
      description: "Interactive risk assessment questionnaire",
      detailedDescription:
        "Display the risk assessment questionnaire configured in your risk model. Collects client responses to determine their risk score, which is then used to recommend appropriate portfolios. Requires a risk model to be configured on the flow.",
    },
    portfolio_display: {
      label: "Portfolio Display",
      description: "Show portfolio recommendations based on risk score",
      detailedDescription:
        "Display the recommended portfolio allocation for the client based on their risk assessment results. Shows the list of products with their target allocations. Can include the portfolio name and description.",
    },
    portfolio_xray: {
      label: "Portfolio X-Ray",
      description: "Detailed portfolio analysis from Morningstar",
      detailedDescription:
        "Display Morningstar X-Ray analysis of the portfolio. Choose from multiple views: asset allocation, country/regional exposure, stock sectors, bond sectors, equity style box, or top holdings. Requires Morningstar X-Ray access.",
    },
    asset_allocation: {
      label: "Asset Allocation",
      description: "Asset class breakdown chart",
      detailedDescription:
        "Display how the portfolio is distributed across asset classes (equities, bonds, cash, alternatives, etc.). Calculated from the underlying product allocations. Can show as a pie chart, table, or both.",
    },
    asset_class_forecast: {
      label: "Asset Class Forecast",
      description: "Asset class mix over time",
      detailedDescription:
        "Display how the asset class mix changes over time as a 100% stacked bar chart. Each portfolio is weighted by its projected value at that date, so the mix shifts as faster-growing portfolios take a larger share. Toggle between 1, 5 and 10 years. On a goal page it covers that goal's portfolios; on a global page, every portfolio of every goal.",
    },
    volatility_chart: {
      label: "Volatility Chart",
      description: "Portfolio volatility over time with compliance gate",
      detailedDescription:
        "Display a line chart showing the goal's blended portfolio volatility over time, with a ceiling line from the risk model's max volatility per risk score. When year-1 volatility breaches the ceiling, the advisor must accept the deviation with a justification or fix the portfolio. Toggle between 1, 5 and 10 year views.",
    },
    equity_share_chart: {
      label: "Equity Share Chart",
      description: "Portfolio equity share over time with compliance gate",
      detailedDescription:
        "Display a stacked bar chart showing the goal's blended equity share over time (equity, non-equity and unknown), with a ceiling line from the risk model's max equity share per risk score. When the year-1 equity share breaches the ceiling, the advisor must accept the deviation with a justification or fix the portfolio. Toggle between 1, 5 and 10 year views.",
    },
    mandate: {
      label: "Mandate",
      description: "The goal's permitted allocation and whether the advice stays within it",
      detailedDescription:
        "Display the mandate that applies to the goal — from the bank, or resolved from the collection's templates by risk score and platform — and check the goal's combined allocation at the end of year 1 against each instrument's permitted weight or band. Where the check bites follows the collection's block level: under goal blocking the goal page carries the verdict, the decision and the gate and the portfolio page is informational; under portfolio blocking each portfolio page carries its own, and the goal page sums them up. A breach blocks submission until the advisor accepts it with a note (where the collection allows it). The portfolio page also offers 'Update mandate from this portfolio'. Renders nothing while the collection has mandates off.",
    },
    mandate_comparison: {
      label: "Mandate Comparison",
      description: "Follow-up: the client's current mandate beside the new one",
      detailedDescription:
        "On a follow-up flow, show the mandate the client holds today beside the one this advice proposes, line by line, with the changed lines marked. Informational — it never blocks, and it renders nothing while the goal has one side only (no bank mandate carried, or no new mandate written beside it).",
    },
    portfolio_forecast: {
      label: "Portfolio Forecast",
      description: "Projected value with confidence bounds",
      detailedDescription:
        "Display a chart showing projected portfolio value over time with 95% confidence bounds (best case, expected, worst case scenarios). Based on historical returns and volatility. Can include statistics and a data table.",
    },
    historical_return: {
      label: "Historical Return Chart",
      description: "Portfolio historical performance",
      detailedDescription:
        "Display a line chart showing how the portfolio would have performed historically based on past market data. Useful for illustrating long-term growth potential. Note: Past performance is not indicative of future results.",
    },
    expected_value: {
      label: "Expected Value",
      description: "Projected portfolio value over time",
      detailedDescription:
        "Show the expected future value of the investment based on forecasted returns. Considers initial investment, regular contributions, and expected growth rate. Can display as a chart, summary table, or detailed yearly breakdown.",
    },
    cost_table: {
      label: "Cost Table",
      description: "Cost breakdown by category",
      detailedDescription:
        "Display a summary table of all investment costs grouped by category: recurring fees (annual charges), deposit fees (entry costs), and withdrawal fees (exit costs). Shows both percentage and monetary amounts.",
    },
    cost_chart: {
      label: "Cost Projection Chart",
      description: "Account value with/without costs",
      detailedDescription:
        "Display a chart comparing projected account value over time with and without costs deducted. Helps clients visualize the long-term impact of fees. Choose between rolling 12-month or calendar year display modes.",
    },
    portfolio_costs: {
      label: "Portfolio Costs",
      description: "Advisor/platform fees with tiered brackets",
      detailedDescription:
        "Display advisor and platform fees as configured in the portfolio collection. Shows fee tiers with their corresponding percentages and the applicable tier based on the investment amount.",
    },
    product_costs: {
      label: "Product Costs",
      description: "Product costs (expense ratios, fees)",
      detailedDescription:
        "Display the costs associated with each product in the portfolio. Includes expense ratios (TER), transaction costs, and any other product-level fees. Shows both individual product costs and weighted average.",
    },
    cost_benefit: {
      label: "Cost Benefit",
      description: "Existing vs new portfolio: return, risk, costs",
      detailedDescription:
        "Compare the client's existing portfolio with the proposed one on expected return, expected volatility and product costs, and show the one-time costs of switching (withdrawal, deposit and currency fees). Renders on followup sessions of one-time portfolios; otherwise shows that there is no existing portfolio. Holdings that could not be mapped to a product are listed with a warning and excluded from the figures.",
    },
    client_classification: {
      label: "Client Classification",
      description: "MiFID II classification questionnaire",
      detailedDescription:
        "Interactive questionnaire to determine the client's MiFID II classification (Retail, Professional, or Eligible Counterparty). Collects information about the client's knowledge, experience, and financial situation to determine appropriate classification.",
    },
    product_assessment: {
      label: "Product Assessment",
      description: "Capture investment experience by product type",
      detailedDescription:
        "Allow clients to indicate their experience with different types of financial products. Captures knowledge level and transaction history for various instrument categories as required for suitability assessment.",
    },
    financial_situation: {
      label: "Financial Situation",
      description: "MiFID II financial assessment",
      detailedDescription:
        "Comprehensive form for capturing client's financial situation including income, assets, liabilities, and regular commitments. Calculates key metrics like net worth and debt ratio. Which fields a client sees — and whether the component applies at all — depends on the client type (Person, Company, Group) and is configured per field on the flow's financial situation model.",
    },
    sustainability: {
      label: "Sustainability",
      description: "Sustainability preferences (SFDR, criteria, product preferences)",
      detailedDescription:
        "Ask whether sustainability is important to the client and capture their preferences per configured criterion (SFDR scale, extra scales, product preferences) plus the portfolio alignment mode. Requires a sustainability configuration on the flow's portfolio collection. Global pages; advisor and public flows.",
    },
    sustainability_status: {
      label: "Sustainability Status",
      description: "Per-goal sustainability matching table",
      detailedDescription:
        "Shows how each product of the goal's portfolios matches the client's sustainability preferences, with a goal-level status. On the advisor flow a deviation can be accepted (or marked do-not-proceed) with a justification. Goal pages only.",
    },
    client_preferences: {
      label: "Client Preferences",
      description: "Client preference yes/no answers",
      detailedDescription:
        "List every client-preference definition of the flow's portfolio collection with an explicit yes/no choice and no default. Every preference must be answered before the flow can be submitted. Definitions are managed on the portfolio collection's Client preferences tab. Global pages; advisor and public flows.",
    },
    quiz: {
      label: "Quiz",
      description: "Knowledge quiz the client must pass",
      detailedDescription:
        "Runs a knowledge quiz you write here, in this component's own settings: the questions, their scored answers, how many are drawn per attempt, the score needed to pass and how many attempts the client gets. Each attempt draws its questions at random and is graded on the server; until the client passes, the flow cannot be submitted. A pass locks permanently, and an advisor can override a failed quiz with a recorded reason. Global pages; advisor and public flows.",
    },
    portfolio_list: {
      label: "Portfolio List",
      description: "List portfolios with payment details",
      detailedDescription:
        "Display a clickable list of portfolios with their names and payment schedules. Includes one-time or recurring payment details. Only visible when there are multiple portfolios. Click a portfolio to navigate to it.",
    },
    account_cash_overview: {
      label: "Account Cash Overview",
      description: "Cash per account after the planned trades and transfers",
      detailedDescription:
        "Show every account of the client with its bank cash, the net cash effect of the flow's portfolios, internal transfers, the deposit needed and the cash left. Expand an account to see its portfolios. Read-only; global and goal pages.",
    },
    cash_transfers: {
      label: "Cash Transfers",
      description: "Plan transfers between the client's accounts",
      detailedDescription:
        "List, add, edit and delete planned cash transfers between the client's accounts, including accounts the flow will open. The amount is in the source account's currency. Every change updates the Account Cash Overview. Global and goal pages.",
    },
    goal_list: {
      label: "Goal List",
      description: "List, add, edit and remove the instance's goals",
      detailedDescription:
        "Show every goal of this flow instance as a card with its type icon, name and platform. Goals can be added, edited and deleted from the list; adding or editing opens a modal with the goal details form. Global pages only.",
    },
    wealth_plan: {
      label: "Wealth Plan",
      description: "Plan future portfolio withdrawals",
      detailedDescription:
        "Allow clients to plan future withdrawals from their investment portfolio. Define withdrawal amounts and timing for retirement planning or other financial goals. Shows a summary of planned distributions.",
    },
    trapets_lookup: {
      label: "PEP/Sanction Lookup",
      description: "KYC/AML screening for PEP and sanctions",
      detailedDescription:
        "Perform PEP (Politically Exposed Person) and sanctions screening against Trapets database. Uses client name, country, and SSN or birth date. Displays matched individuals, PEP roles, and sanction sources.",
    },
    sumsub_verification: {
      label: "Sumsub Identity Verification",
      description: "KYC identity verification with document and biometric checks",
      detailedDescription:
        "Comprehensive identity verification powered by Sumsub. Supports document verification (passport, ID card, driver's license) and biometric checks with liveness detection. Provides real-time verification status and detailed results.",
    },
    enin_lookup: {
      label: "Company Lookup",
      description: "Company registry and risk screening via Enin",
      detailedDescription:
        "Fetches company registry data, financial highlights, ownership and risk flags from Enin. Only applicable to Company clients (not Person). Can only be placed on global (client-level) pages.",
    },
    text_block: {
      label: "Text Block",
      description: "Static text or heading for context",
      detailedDescription:
        "Add formatted text content to your flow page. Supports one heading style (rendered like every component header) plus rich text: bold, italic, lists and links. Useful for adding instructions, explanations, or section headers to guide users through the flow.",
    },
    spacer: {
      label: "Spacer",
      description: "Vertical spacing between components",
      detailedDescription:
        "Add vertical whitespace between components. Configure the height in pixels (1-500). Useful for improving visual separation between sections without adding visible content.",
    },
    youtube_video: {
      label: "YouTube Video",
      description: "Embed a YouTube video",
      detailedDescription:
        "Embed a YouTube video directly in your flow page. Great for educational content, welcome messages, or explaining complex concepts. Just paste the YouTube URL and the video will be embedded automatically.",
    },
    iframe: {
      label: "Web page (URL)",
      description: "Show a web page, video or form from an https URL",
      detailedDescription:
        "Embed a third-party page by its address — a YouTube or Vimeo video, a Typeform or Google Form, a fund factsheet. It is shown in a sandboxed frame that keeps the site's own origin, so players and forms work, but it can never act as this app. The site must allow being embedded (no X-Frame-Options: DENY) — the preview shows what clients will see.",
    },
    image: {
      label: "Image",
      description: "Display an image (PNG, SVG, JPG)",
      detailedDescription:
        "Upload and display an image on your flow page. Supports PNG, SVG, and JPG formats. Useful for branding, diagrams, or visual explanations. Images are stored securely and served through CDN.",
    },
    embed: {
      label: "Custom Embed",
      description: "Embed custom HTML content",
      detailedDescription:
        "Embed custom HTML and scripts, rendered in an isolated sandbox. Useful for small widgets such as calculators. To show a YouTube video, a form or another web page, use the Web page (URL) component instead.",
    },
    modal: {
      label: "Info Modal",
      description: "Click-to-open popup with additional information",
      detailedDescription:
        "Add a button that opens a popup with additional information. Configure the button label, the popup title, and the content using the same markdown formatting as the Text Block. Useful for educational or 'learn more' content that shouldn't clutter the page.",
    },
    file: {
      label: "File Download",
      description: "Uploaded document shown as a download link",
      detailedDescription:
        "Upload a document (PDF, Word, Excel, CSV or text) and display it as a download link that opens in a new tab. Useful for methodology guides, terms documents, or other reference material. Files are stored securely per tenant.",
    },
  },
  nb_NO: {
    field: {
      label: "Feltinndata",
      description: "Legg til et inndatafelt fra flytens feltbibliotek",
      detailedDescription:
        "Vis et skjemafelt som samler inn data fra brukeren. Velg fra felt definert i flytens feltbibliotek. Støtter ulike inndatatyper inkludert tekst, tall, dato, nedtrekksmeny og mer.",
    },
    goal_input: {
      label: "Mål-inndataskjema",
      description: "Skjema for redigering av måldetaljer (navn, type)",
      detailedDescription:
        "Et skjema som lar rådgivere konfigurere måldetaljer på målnivå — målnavn og type. Investeringsplan, beløp og porteføljetype konfigureres per portefølje med Portefølje-inndataskjemaet. Hører hjemme på en målside.",
    },
    portfolio_input: {
      label: "Portefølje-inndataskjema",
      description: "Skjema for redigering av en porteføljes plan, beløp og type",
      detailedDescription:
        "Et skjema som lar rådgivere konfigurere en portefølje: navn, investeringsplan (engangsinnskudd eller gjentakende innskudd) og beløp, og valg av porteføljetype (modell eller tilpasset). Hører hjemme på porteføljesiden; vises én gang per portefølje.",
    },
    custom_portfolio: {
      label: "Tilpasset porteføljebygger",
      description: "Bygg tilpassede porteføljer ved å velge produkter",
      detailedDescription:
        "En interaktiv komponent som lar rådgivere bygge tilpassede porteføljer ved å søke og velge produkter, og deretter spesifisere investeringsbeløp eller prosenter for hvert produkt. Inkluderer alternativer for å vise ISIN og produkttype-kolonner.",
    },
    risk_assessment: {
      label: "Risikovurdering",
      description: "Interaktivt risikovurderingsskjema",
      detailedDescription:
        "Vis risikovurderingsskjemaet konfigurert i risikomodellen din. Samler kundesvar for å bestemme risikoscore, som deretter brukes til å anbefale passende porteføljer. Krever at en risikomodell er konfigurert på flyten.",
    },
    portfolio_display: {
      label: "Porteføljevisning",
      description: "Vis porteføljeanbefalinger basert på risikoscore",
      detailedDescription:
        "Vis den anbefalte porteføljeallokeringen for kunden basert på risikovurderingsresultatene. Viser listen over produkter med måltildelinger. Kan inkludere porteføljenavn og beskrivelse.",
    },
    portfolio_xray: {
      label: "Portefølje X-Ray",
      description: "Detaljert porteføljeanalyse fra Morningstar",
      detailedDescription:
        "Vis Morningstar X-Ray-analyse av porteføljen. Velg mellom flere visninger: aktivaallokering, land/regional eksponering, aksjesektorer, obligasjonssektorer, aksjestilboks, eller toppholdninger. Krever Morningstar X-Ray-tilgang.",
    },
    asset_allocation: {
      label: "Aktivaallokering",
      description: "Aktivaklassefordeling",
      detailedDescription:
        "Vis hvordan porteføljen er fordelt på aktivaklasser (aksjer, obligasjoner, kontanter, alternativer, osv.). Beregnet fra underliggende produktallokeringer. Kan vises som sektordiagram, tabell eller begge.",
    },
    asset_class_forecast: {
      label: "Aktivaklasseprognose",
      description: "Aktivaklassefordeling over tid",
      detailedDescription:
        "Vis hvordan fordelingen mellom aktivaklasser endrer seg over tid som et 100 % stablet stolpediagram. Hver portefølje vektes med sin forventede verdi på den datoen, slik at fordelingen forskyves når porteføljer vokser ulikt. Velg mellom 1, 5 og 10 år. På en målside dekker den målets porteføljer; på en global side alle porteføljer i alle mål.",
    },
    volatility_chart: {
      label: "Volatilitetsdiagram",
      description: "Porteføljevolatilitet over tid med samsvarskontroll",
      detailedDescription:
        "Vis et linjediagram som viser målets samlede porteføljevolatilitet over tid, med en takgrense fra risikomodellens maksimale volatilitet per risikoscore. Når volatiliteten i år 1 overstiger grensen, må rådgiveren akseptere avviket med begrunnelse eller endre porteføljen. Velg mellom 1, 5 og 10 års visning.",
    },
    equity_share_chart: {
      label: "Aksjeandelsdiagram",
      description: "Porteføljens aksjeandel over tid med samsvarskontroll",
      detailedDescription:
        "Vis et stablet stolpediagram som viser målets samlede aksjeandel over tid (aksjer, ikke-aksjer og ukjent), med en takgrense fra risikomodellens maksimale aksjeandel per risikoscore. Når aksjeandelen i år 1 overstiger grensen, må rådgiveren akseptere avviket med begrunnelse eller endre porteføljen. Velg mellom 1, 5 og 10 års visning.",
    },
    mandate: {
      label: "Mandat",
      description: "Målets tillatte allokering og om rådet holder seg innenfor",
      detailedDescription:
        "Vis mandatet som gjelder for målet — fra banken, eller utledet fra samlingens maler etter risikoscore og plattform — og kontroller målets samlede allokering ved utgangen av år 1 mot hvert instruments tillatte vekt eller intervall. Hvor kontrollen gjelder følger samlingens blokkeringsnivå: ved blokkering per mål har målsiden vurderingen, beslutningen og sperren, og porteføljesiden er til informasjon; ved blokkering per portefølje har hver porteføljeside sin egen, og målsiden oppsummerer dem. Et brudd blokkerer innsending til rådgiveren aksepterer det med en begrunnelse (der samlingen tillater det). Porteføljesiden tilbyr også «Oppdater mandat fra denne porteføljen». Viser ingenting mens samlingen har mandater av.",
    },
    mandate_comparison: {
      label: "Mandatsammenligning",
      description: "Oppfølging: kundens nåværende mandat ved siden av det nye",
      detailedDescription:
        "I en oppfølgingsflyt vises mandatet kunden har i dag ved siden av det dette rådet foreslår, linje for linje, med endrede linjer markert. Informativt — blokkerer aldri, og viser ingenting mens målet bare har én side (ingen bankmandat medfølger, eller ingen nytt mandat er skrevet ved siden av).",
    },
    portfolio_forecast: {
      label: "Porteføljeprognose",
      description: "Forventet verdi med konfidensintervall",
      detailedDescription:
        "Vis et diagram som viser forventet porteføljeverdi over tid med 95% konfidensintervall (beste tilfelle, forventet, verste tilfelle). Basert på historisk avkastning og volatilitet. Kan inkludere statistikk og datatabell.",
    },
    historical_return: {
      label: "Historisk avkastning",
      description: "Porteføljens historiske utvikling",
      detailedDescription:
        "Vis et linjediagram som viser hvordan porteføljen ville ha utviklet seg historisk basert på tidligere markedsdata. Nyttig for å illustrere langsiktig vekstpotensial. Merk: Historisk avkastning er ingen garanti for fremtidig avkastning.",
    },
    expected_value: {
      label: "Forventet verdi",
      description: "Forventet porteføljeverdi over tid",
      detailedDescription:
        "Vis forventet fremtidig verdi av investeringen basert på prognostisert avkastning. Tar hensyn til startinvestering, regelmessige innskudd og forventet vekstrate. Kan vises som diagram, sammendragstabell eller detaljert årlig oversikt.",
    },
    cost_table: {
      label: "Kostnadstabell",
      description: "Kostnadsfordeling etter kategori",
      detailedDescription:
        "Vis en sammendragstabell over alle investeringskostnader gruppert etter kategori: løpende gebyrer (årlige avgifter), innskuddsgebyrer (inngangskostnader) og uttaksgebyrer (utgangskostnader). Viser både prosent og beløp.",
    },
    cost_chart: {
      label: "Kostnadsframskrivning",
      description: "Kontoverdi med/uten kostnader",
      detailedDescription:
        "Vis et diagram som sammenligner forventet kontoverdi over tid med og uten kostnader fratrukket. Hjelper kunder å visualisere langsiktig effekt av gebyrer. Velg mellom rullerende 12-måneders eller kalenderårsvisning.",
    },
    portfolio_costs: {
      label: "Porteføljekostnader",
      description: "Rådgiver-/plattformgebyrer med trinnvise satser",
      detailedDescription:
        "Vis rådgiver- og plattformgebyrer som konfigurert i porteføljesamlingen. Viser gebyrnivåer med tilhørende prosenter og gjeldende nivå basert på investeringsbeløpet.",
    },
    product_costs: {
      label: "Produktkostnader",
      description: "Produktkostnader (forvaltningshonorar, gebyrer)",
      detailedDescription:
        "Vis kostnadene knyttet til hvert produkt i porteføljen. Inkluderer forvaltningshonorar (TER), transaksjonskostnader og andre produktnivågebyrer. Viser både individuelle produktkostnader og vektet gjennomsnitt.",
    },
    cost_benefit: {
      label: "Kost/nytte",
      description: "Eksisterende vs ny portefølje: avkastning, risiko, kostnader",
      detailedDescription:
        "Sammenlign kundens eksisterende portefølje med den foreslåtte på forventet avkastning, forventet volatilitet og produktkostnader, og vis engangskostnadene ved å bytte (uttaks-, innskudds- og valutagebyrer). Vises i oppfølgingsøkter for engangsporteføljer; ellers vises det at det ikke finnes noen eksisterende portefølje. Beholdninger som ikke kunne kobles til et produkt, listes med en advarsel og holdes utenfor tallene.",
    },
    client_classification: {
      label: "Kundeklassifisering",
      description: "MiFID II-klassifiseringsskjema",
      detailedDescription:
        "Interaktivt skjema for å bestemme kundens MiFID II-klassifisering (Ikke-profesjonell, Profesjonell eller Kvalifisert motpart). Samler informasjon om kundens kunnskap, erfaring og økonomiske situasjon for å bestemme passende klassifisering.",
    },
    product_assessment: {
      label: "Produktvurdering",
      description: "Registrer investeringserfaring etter produkttype",
      detailedDescription:
        "La kunder angi sin erfaring med ulike typer finansielle produkter. Registrerer kunnskapsnivå og transaksjonshistorikk for ulike instrumentkategorier som kreves for egnethetsvurdering.",
    },
    financial_situation: {
      label: "Finansiell situasjon",
      description: "MiFID II økonomisk vurdering",
      detailedDescription:
        "Omfattende skjema for å registrere kundens økonomiske situasjon inkludert inntekt, eiendeler, gjeld og faste forpliktelser. Beregner nøkkeltall som nettoverdi og gjeldsgrad. Hvilke felter en kunde ser — og om komponenten i det hele tatt gjelder — avhenger av kundetypen (person, selskap, gruppe) og konfigureres per felt på flytens modell for finansiell situasjon.",
    },
    sustainability: {
      label: "Bærekraft",
      description: "Bærekraftspreferanser (SFDR, kriterier, produktpreferanser)",
      detailedDescription:
        "Spør om bærekraft er viktig for kunden og registrer preferansene per konfigurert kriterium (SFDR-skala, ekstra skalaer, produktpreferanser) samt porteføljetilpasningsmodus. Krever en bærekraftskonfigurasjon på flytens porteføljesamling. Globale sider; rådgiver- og kundeflyt.",
    },
    sustainability_status: {
      label: "Bærekraftsstatus",
      description: "Bærekraftsmatching per mål",
      detailedDescription:
        "Viser hvordan hvert produkt i målets porteføljer matcher kundens bærekraftspreferanser, med en status på målnivå. I rådgiverflyten kan et avvik aksepteres (eller markeres som ikke videre) med en begrunnelse. Kun målsider.",
    },
    client_preferences: {
      label: "Kundepreferanser",
      description: "Kundens ja/nei-svar på preferanser",
      detailedDescription:
        "Vis hver kundepreferanse definert på flytens porteføljesamling med et eksplisitt ja/nei-valg og ingen standardverdi. Alle preferanser må besvares før flyten kan sendes inn. Definisjonene administreres på porteføljesamlingens Kundepreferanser-fane. Globale sider; rådgiver- og kundeflyt.",
    },
    quiz: {
      label: "Quiz",
      description: "Kunnskapsquiz kunden må bestå",
      detailedDescription:
        "Kjører en kunnskapsquiz du lager her, i komponentens egne innstillinger: spørsmålene, svaralternativene med poeng, hvor mange som trekkes per forsøk, poengsummen som kreves for å bestå og hvor mange forsøk kunden får. Hvert forsøk trekker spørsmålene tilfeldig og rettes på serveren; flyten kan ikke sendes inn før kunden har bestått. Bestått låses permanent, og en rådgiver kan overstyre en ikke bestått quiz med en registrert begrunnelse. Globale sider; rådgiver- og kundeflyt.",
    },
    portfolio_list: {
      label: "Porteføljeliste",
      description: "List porteføljer med betalingsdetaljer",
      detailedDescription:
        "Vis en klikkbar liste over porteføljer med navn og betalingsplaner. Inkluderer engangs- eller gjentakende betalingsdetaljer. Vises kun når det finnes flere porteføljer. Klikk på en portefølje for å navigere til den.",
    },
    account_cash_overview: {
      label: "Kontantoversikt per konto",
      description: "Kontanter per konto etter planlagte handler og overføringer",
      detailedDescription:
        "Vis alle kundens kontoer med bankkontanter, netto kontanteffekt av flytens porteføljer, interne overføringer, nødvendig innskudd og gjenværende kontanter. Utvid en konto for å se porteføljene. Kun visning; globale sider og målsider.",
    },
    cash_transfers: {
      label: "Kontantoverføringer",
      description: "Planlegg overføringer mellom kundens kontoer",
      detailedDescription:
        "List, legg til, rediger og slett planlagte kontantoverføringer mellom kundens kontoer, også kontoer flyten vil opprette. Beløpet er i fra-kontoens valuta. Hver endring oppdaterer kontantoversikten. Globale sider og målsider.",
    },
    goal_list: {
      label: "Målliste",
      description: "List, legg til, rediger og fjern målene i flyten",
      detailedDescription:
        "Vis alle målene i denne flytinstansen som kort med typeikon, navn og plattform. Mål kan legges til, redigeres og slettes fra listen; å legge til eller redigere åpner en dialog med målskjemaet. Kun globale sider.",
    },
    wealth_plan: {
      label: "Formuesplan",
      description: "Planlegg fremtidige porteføljeuttrekk",
      detailedDescription:
        "La kunder planlegge fremtidige uttak fra investeringsporteføljen. Definer uttaksbeløp og tidspunkt for pensjonsplanlegging eller andre økonomiske mål. Viser et sammendrag av planlagte utbetalinger.",
    },
    trapets_lookup: {
      label: "PEP/Sanksjonssjekk",
      description: "KYC/AML-screening for PEP og sanksjoner",
      detailedDescription:
        "Utfør PEP (Politisk eksponert person) og sanksjonsscreening mot Trapets-databasen. Bruker kundens navn, land og personnummer eller fødselsdato. Viser treff på personer, PEP-roller og sanksjonskilder.",
    },
    sumsub_verification: {
      label: "Sumsub Identitetsverifisering",
      description: "KYC-identitetsverifisering med dokument- og biometrikontroll",
      detailedDescription:
        "Omfattende identitetsverifisering drevet av Sumsub. Støtter dokumentverifisering (pass, ID-kort, førerkort) og biometriske kontroller med livlighetspåvisning. Gir sanntids verifiseringsstatus og detaljerte resultater.",
    },
    enin_lookup: {
      label: "Selskapsoppslag",
      description: "Selskapsregister- og risikosøk via Enin",
      detailedDescription:
        "Henter selskapsdata, regnskapstall, eierskap og risikoflagg fra Enin. Gjelder kun selskapskunder (ikke privatpersoner). Kan bare plasseres på globale sider (kundenivå).",
    },
    text_block: {
      label: "Tekstblokk",
      description: "Statisk tekst eller overskrift for kontekst",
      detailedDescription:
        "Legg til formatert tekstinnhold på flytsiden din. Støtter én overskriftsstil (vises som alle komponentoverskrifter) samt rik tekst: fet, kursiv, lister og lenker. Nyttig for å legge til instruksjoner, forklaringer eller seksjonstitler for å veilede brukere gjennom flyten.",
    },
    spacer: {
      label: "Mellomrom",
      description: "Vertikal avstand mellom komponenter",
      detailedDescription:
        "Legg til vertikalt mellomrom mellom komponenter. Konfigurer høyden i piksler (1-500). Nyttig for å forbedre visuell separasjon mellom seksjoner uten å legge til synlig innhold.",
    },
    youtube_video: {
      label: "YouTube-video",
      description: "Bygg inn en YouTube-video",
      detailedDescription:
        "Bygg inn en YouTube-video direkte på flytsiden din. Flott for opplæringsinnhold, velkomstmeldinger eller forklaring av komplekse konsepter. Bare lim inn YouTube-URL og videoen blir automatisk innebygd.",
    },
    iframe: {
      label: "Nettside (URL)",
      description: "Vis en nettside, video eller skjema fra en https-URL",
      detailedDescription:
        "Bygg inn en tredjeparts side via adressen – en YouTube- eller Vimeo-video, et Typeform- eller Google-skjema, et faktaark. Den vises i en sandkasse-ramme som beholder nettstedets eget opphav, så spillere og skjemaer virker, men den kan aldri opptre som denne appen. Nettstedet må tillate innbygging (ingen X-Frame-Options: DENY) – forhåndsvisningen viser hva kundene ser.",
    },
    image: {
      label: "Bilde",
      description: "Vis et bilde (PNG, SVG, JPG)",
      detailedDescription:
        "Last opp og vis et bilde på flytsiden din. Støtter PNG, SVG og JPG-formater. Nyttig for merkevarebygging, diagrammer eller visuelle forklaringer. Bilder lagres sikkert og leveres via CDN.",
    },
    embed: {
      label: "Egendefinert innbygging",
      description: "Bygg inn tilpasset HTML-innhold",
      detailedDescription:
        "Bygg inn tilpasset HTML og skript, vist i en isolert sandkasse. Nyttig for små widgeter som kalkulatorer. For å vise en YouTube-video, et skjema eller en annen nettside, bruk komponenten Nettside (URL) i stedet.",
    },
    modal: {
      label: "Infovindu",
      description: "Klikkbart popup-vindu med tilleggsinformasjon",
      detailedDescription:
        "Legg til en knapp som åpner et popup-vindu med tilleggsinformasjon. Konfigurer knappeteksten, tittelen på vinduet og innholdet med samme markdown-formatering som Tekstblokk. Nyttig for utdypende eller 'les mer'-innhold som ikke bør fylle siden.",
    },
    file: {
      label: "Filnedlasting",
      description: "Opplastet dokument vist som nedlastingslenke",
      detailedDescription:
        "Last opp et dokument (PDF, Word, Excel, CSV eller tekst) og vis det som en nedlastingslenke som åpnes i en ny fane. Nyttig for metodedokumenter, vilkår eller annet referansemateriale. Filer lagres sikkert.",
    },
  },
};
